import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { isVideoClipTask } from '~/components/VideoClipTasks/isVideoClipTask';
import { VideoClipTasksPanel } from '~/components/VideoClipTasks/VideoClipTasksPanel';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSocketConnectionChange } from '~/providers/SocketContext/hooks/useSocketConnectionChange';
import { removeTasksAction } from '~/store/tasks/actions';
import { makeAssetTransformationTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { ReadyState } from '~/types/sockets';
import { useAirSelector } from '~/utils/ReduxUtils';
import { useCancelTask } from '~/utils/taskUtils/useCancelTask';
import { usePrivateSyncTasks } from '~/utils/taskUtils/usePrivateSyncTasks';

export const PrivateVideoClipTasksPanel = memo(() => {
  const { currentWorkspace } = useCurrentWorkspace();
  const dispatch = useDispatch();
  const { cancelTask } = useCancelTask();

  const tasksSelector = useMemo(
    () => makeAssetTransformationTasksSelectorByWorkspaceId(currentWorkspace?.id),
    [currentWorkspace?.id],
  );

  const allTransformationTasks = useAirSelector(tasksSelector);
  const videoClipTasks = useMemo(() => allTransformationTasks.filter(isVideoClipTask), [allTransformationTasks]);

  const {
    loadFromStorage,
    syncLocalTasks,
    subscriptionsReady: taskSubscriptionsReady,
  } = usePrivateSyncTasks({
    workspaceId: currentWorkspace?.id,
    tasksSelector,
    localType: 'AssetTransformation',
    remoteType: 'AssetTransformer',
  });

  const syncExecutedForReadyStateRef = useRef(false);

  useEffect(() => {
    if (currentWorkspace?.id) {
      loadFromStorage();
    }
  }, [loadFromStorage, currentWorkspace?.id]);

  const syncLocalTasksOnce = useCallback(() => {
    if (!syncExecutedForReadyStateRef.current) {
      syncLocalTasks();
      syncExecutedForReadyStateRef.current = true;
    }
  }, [syncLocalTasks]);

  useEffect(() => {
    if (!taskSubscriptionsReady) {
      syncExecutedForReadyStateRef.current = false;
    }
  }, [taskSubscriptionsReady]);

  useSocketConnectionChange({
    onChange: (readyState) => {
      if (readyState === ReadyState.OPEN && taskSubscriptionsReady) {
        syncLocalTasksOnce();
      }
    },
  });

  useEffect(() => {
    if (taskSubscriptionsReady) {
      syncLocalTasksOnce();
    }
  }, [taskSubscriptionsReady, syncLocalTasksOnce]);

  useUnmount(() => {
    dispatch(
      removeTasksAction({
        taskIds: videoClipTasks.map(({ localTaskId }) => localTaskId),
      }),
    );
  });

  if (videoClipTasks.length < 1) {
    return null;
  }

  return <VideoClipTasksPanel tasks={videoClipTasks} onCancel={cancelTask} onClear={cancelTask} />;
});

PrivateVideoClipTasksPanel.displayName = 'PrivateVideoClipTasksPanel';
